import React, { useEffect, useState } from 'react';
import { Headset } from 'lucide-react';

interface VRButtonProps {
  onEnterVR?: () => void;
}

const isNativeApp = typeof navigator !== 'undefined' && /GathrXR/i.test(navigator.userAgent);
const isQuest = typeof navigator !== 'undefined' && /Quest|OculusBrowser|GathrXR/i.test(navigator.userAgent);

const VRButton: React.FC<VRButtonProps> = ({ onEnterVR }) => {
  const [isSupported, setIsSupported] = useState(false);

  // Check again until it says yes: a WebXR emulator (iwer, the Immersive Web
  // Emulator extension) installs navigator.xr after the page has mounted, and
  // a headset can be connected later — a single check on mount missed both.
  useEffect(() => {
    if (isSupported) return;
    let alive = true;
    const check = () => {
      const xr = 'xr' in navigator ? navigator.xr : undefined;
      if (xr) xr.isSessionSupported('immersive-vr').then((ok) => { if (alive && ok) setIsSupported(true); }).catch(() => {});
      else if (isQuest) setIsSupported(true);
    };
    check();
    const timers = [500, 1500, 3000, 6000].map((ms) => setTimeout(check, ms));
    const xr = 'xr' in navigator ? navigator.xr : undefined;
    xr?.addEventListener?.('devicechange', check);
    window.addEventListener('focus', check);
    return () => {
      alive = false;
      timers.forEach(clearTimeout);
      xr?.removeEventListener?.('devicechange', check);
      window.removeEventListener('focus', check);
    };
  }, [isSupported]);

  if (!isSupported) {
    return null;
  }

  const handleClick = () => {
    if (isNativeApp) {
      // Native app: open current world in Quest Browser for WebXR
      // Use window.open which triggers shouldOverrideUrlLoading with a non-gathrxr URL
      // The native app's WebViewClient opens external URLs in the system browser (Quest Browser)
      const currentPath = window.location.pathname + window.location.search;
      window.open(`https://gathrxr.com${currentPath}?vr=1`, '_blank');
    } else if (onEnterVR) {
      onEnterVR();
    }
  };

  return (
    <button
      className="btn-glass"
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        cursor: 'pointer',
        border: '1px solid rgba(0, 194, 179, 0.3)',
        background: 'rgba(0, 194, 179, 0.1)',
        padding: '6px 12px',
        borderRadius: '8px',
        color: '#00c2b3',
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      <Headset size={14} />
      <span>{isNativeApp ? 'Open in VR' : 'Enter VR'}</span>
    </button>
  );
};

export default VRButton;
